import { useState, useEffect, useRef } from "react";
import { Camera, Trash2, PenLine } from "lucide-react";
import { useUser, useCurrentUser, useTotalStaff } from "../Use-auth";
import RoleBasedLayout from "./RoleBasedLayout";
import { useQueryClient } from "@tanstack/react-query";

export default function Account() {
  const { data: user } = useUser();
  const { data: total } = useTotalStaff();
  const [profileImage, setProfileImage] = useState(null);
  const [editableDescription, setEditableDescription] = useState("");
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [saving, setSaving] = useState(false);

  const textareaRef = useRef(null);
  const editContainerRef = useRef(null);

  const updateUserMutation = useCurrentUser();
  const queryClient = useQueryClient();

  const appendCreatedBy = (formData) => {
    formData.append("createdBy.userId", user.userId);
    formData.append("createdBy.fullName", user.firstName || user.username);
    formData.append("createdBy.role", user.role || "");
  };

  useEffect(() => {
    if (user && total) {
      const currentUserFromStaff = total.find(
        (emp) => emp.employeeId === user.userId
      );

      setEditableDescription(
        user.workInformation?.designation ||
        user.designation ||
        currentUserFromStaff?.designation ||
        ""
      );

      setProfileImage(
        user.workInformation?.image ||
        user.image ||
        currentUserFromStaff?.image ||
        null
      );
    }
  }, [user, total]);

  const getImageUrl = (path) => {
    if (!path) return null;

    if (path.startsWith("http") || path.startsWith("blob:")) {
      return path;
    }

    const BASE_URL = process.env.REACT_APP_API_URL;

    return `${BASE_URL}${path}`;
  };

  const handleImageChange = (file) => {
    setProfileImage(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("image", file);
    appendCreatedBy(formData);

    updateUserMutation.mutate(
      { employeeId: user.userId, data: formData },
      {
        onSuccess: (updatedEmployee) => {
          setProfileImage(
            updatedEmployee.workInformation?.image ||
            updatedEmployee.image ||
            null
          );

          queryClient.invalidateQueries(["user"]);
        },
      }
    );
  };

  const handleDescriptionSave = () => {
    setSaving(true);

    const formData = new FormData();
    formData.append(
      "designation",
      editableDescription.trim() || "Add designation"
    );
    appendCreatedBy(formData);

    updateUserMutation.mutate(
      { employeeId: user.userId, data: formData },
      {
        onSuccess: (updatedEmployee) => {
          setEditableDescription(
            updatedEmployee.workInformation?.designation ||
            updatedEmployee.designation ||
            ""
          );

          setIsEditingDescription(false);
        },
        onError: () => {
          alert("Failed to update");
        },
        onSettled: () => {
          setSaving(false);
          queryClient.invalidateQueries(["user"]);
        },
      }
    );
  };

  useEffect(() => {
    if (isEditingDescription && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
      textarea.focus();
    }
  }, [isEditingDescription]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isEditingDescription &&
        editContainerRef.current &&
        !editContainerRef.current.contains(event.target)
      ) {
        setIsEditingDescription(false);
        setEditableDescription(
          user?.workInformation?.designation || user?.designation || ""
        );
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditingDescription, user]);

  // remove image
  const handleRemoveImage = () => {
    if (!window.confirm("Remove profile image?")) return;

    const formData = new FormData();
    formData.append("removeImage", "true");
    appendCreatedBy(formData);

    updateUserMutation.mutate(
      { employeeId: user.userId, data: formData },
      {
        onSuccess: () => {
          setProfileImage(null);
          queryClient.invalidateQueries(["user"]);
        },
      }
    );
  };

  return (
    <RoleBasedLayout>
      <div className=" bg-gray-50 flex justify-center items-start py-12 px-2 sm:px-6 lg:px-8">
        <div className="w-full max-w-md bg-white rounded-md sm:rounded-2xl shadow-lg border border-gray-200 p-3 sm:p-6">
          <div className="flex flex-col items-center">
            {/* Profile Image */}
            <div className="relative w-28 h-28 rounded-full overflow-hidden shadow-md flex-shrink-0 group">
              {profileImage ? (
                <img
                  src={
                    profileImage.startsWith("blob:")
                      ? profileImage
                      : getImageUrl(profileImage)
                  }
                  className="w-full h-full object-cover"
                />

              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-3xl">
                  {user?.fullName
                    ? user.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                    : ""}
                </div>
              )}

              {/* Remove Image */}
              {profileImage && (
                <div
                  onClick={handleRemoveImage}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md cursor-pointer hover:scale-110 transition hidden group-hover:block"
                  title="Remove Profile Image"
                >
                  <Trash2 color="red" size={18} />
                </div>
              )}

              {/* Change Image */}
              <div className="absolute bottom-2 right-2">
                <label className="bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-gray-100 transition flex items-center justify-center">
                  <Camera size={18} />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith("image/")) {
                        alert("Only image files are allowed");
                        e.target.value = "";
                        return;
                      }
                      const maxSize = 618 * 1024;
                      if (file.size > maxSize) {
                        alert("File size must be 618 Kb or less");
                        e.target.value = "";
                        return;
                      }
                      handleImageChange(file);
                    }}
                  />
                </label>
              </div>
            </div>

            {/* User Info */}
            <div className="mt-4 text-center space-y-2 w-full" >
              <h2 className="text-xl font-semibold text-gray-900">{user?.fullName || user?.username}</h2>

              {/* Designation */}
              <div className="flex flex-col items-center mt-2" ref={editContainerRef}>
                {isEditingDescription ? (
                  <div className="flex items-center space-x-2 w-full justify-center">
                    <textarea
                      ref={textareaRef}
                      className="resize-none border border-gray-300 rounded-md px-2 py-1 text-center text-xs sm:text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={editableDescription}
                      onChange={(e) => setEditableDescription(e.target.value)}
                      rows={1}
                      onInput={(e) => {
                        const textarea = e.target;
                        textarea.style.height = "auto";
                        textarea.style.height = textarea.scrollHeight + "px";
                      }}
                    />
                    <button
                      onClick={handleDescriptionSave}
                      disabled={saving}
                      className="bg-blue-500 text-white px-3 py-1 rounded-md text-xs sm:text-sm hover:bg-blue-600 transition flex items-center gap-2"
                    >
                      {saving ? (
                        <>
                          Saving...
                          <span className="loader-circle"></span>
                        </>
                      ) : (
                        "Save"
                      )}
                    </button>
                  </div>
                ) : (
                  <p
                    className="flex items-center space-x-1 text-gray-600 text-sm sm:text-base cursor-pointer hover:text-gray-800 font-medium whitespace-pre-line text-center"
                    onClick={() => setIsEditingDescription(true)}
                  >
                    {editableDescription || "Add Designation"}
                    <PenLine size={16} />
                  </p>
                )}
              </div>

              <div className="mt-3 text-gray-600 text-xs sm:text-sm space-y-1">
                <p>
                  <span className="font-medium">User ID:</span> {user?.userId}
                </p>
                <p>
                  <span className="font-medium">Email:</span> {user?.username}
                </p>
                <p>
                  <span className="font-medium">Department:</span> {user?.department || "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RoleBasedLayout>
  );
}